// formsConfig.ts

import { FormConfig, RulesConfig } from "../../../interfaces/dynamicForms/Forms";
import { getWaiverTypes } from "../../../services/Lookups/WaiverLookupService";
import { getAllParticipants } from "../../../services/participantServices/ParticipantService";

export const scdFormConfig: Record<string, FormConfig> = {
  // We store exactly one "SCD_Form" (or rename to your doc's name)
  Family_Funding_Request: {
    form: {
      id: 12314, 
      name: "Family_Funding_Request", // Unique ID for this form type
    },
    steps: [
      {
        id: "ff_step1",
        label: "Basic Information",
        icon: 'faUser', // Reusing icon, adjust if needed
        questions: [
          {
            id: "ff_dateOfContact",
            label: "Date of Contact",
            type: "date",
            format: "MM/dd/yyyy",
            validation: { required: true },
            // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_formCompletedBy",
            label: "Form Completed By",
            type: "text",
            placeholder: "Enter name of person completing form",
            validation: { required: true },
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_clientName",
            label: "Client",
            type: "text", // Consider if this should be a dynamic 'participantSelect' like in SCD_Form
            placeholder: "Enter client's full name",
            validation: { required: true },
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_clientAge",
            label: "Age",
            type: "text",
            validation: { required: true },
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_clientDiagnosis",
            label: "Diagnosis",
            type: "text",
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_clientSOE", // Assuming SOE means Start of Eligibility or similar
            label: "SOE",
            type: "text", // Or 'date' if it's a date field
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_primaryContactName",
            label: "Primary Contact",
            type: "text",
            placeholder: "Enter primary contact's name",
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_primaryContactPhone",
            label: "Phone #",
            type: "text", // Use 'tel' type for phone numbers
            placeholder: "Enter phone number",
             // source: PDF Page 1 [cite: 1]
          },
        ],
      },
      {
        id: "ff_step2",
        label: "Service Request & Priority",
        icon: 'faClipboardQuestion', // Reusing icon
        questions: [
          {
            id: "ff_servicesRequested",
            label: "Service(s) requested",
            type: "checkbox", // Use checkbox for multiple selections
            options: [
              "PCA",
              "Respite",
              "Placement",
              "Companion",
              "Vocational",
              "Supplies",
              "Modifications",
              "Other",
            ],
            validation: { required: true },
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_servicesRequestedOther",
            label: "Other Service (Specify)",
            type: "text",
            placeholder: "Specify other service",
            showIf: [ // Show only if 'Other' is checked in the previous question
              {
                field: "ff_servicesRequested",
                operator: "CONTAINS",
                value: "Other",
              },
            ],
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_comments",
            label: "Comments",
            type: "paragraph", // For multi-line text
            placeholder: "Enter any relevant comments",
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_priorityMeetingDate",
            label: "Priority Review Committee Meeting Date",
            type: "date",
            format: "MM/dd/yyyy",
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_priorityJustification",
            label: "Justification",
            type: "paragraph",
            placeholder: "Enter justification for priority",
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_priorityCspAssigned",
            label: "CSP Assigned", // Assuming CSP means Community Service Professional or similar
            type: "text",
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_priorityCaseReviewedBy",
            label: "Case Reviewed By",
            type: "text",
             // source: PDF Page 1 [cite: 1]
          },
          {
            id: "ff_priorityLevel",
            label: "Priority Level",
            type: "radio", // Use radio for single selection
            options: ["1", "2", "3", "4"],
            validation: { required: true },
             // source: PDF Page 1 [cite: 1]
          },
        ],
      },
      {
        id: "ff_step3",
        label: "Financial Information",
        icon: 'faFileInvoiceDollar', // Specific icon for finance
        questions: [
           {
            id: "ff_fin_name", // Consider pre-filling from ff_clientName
            label: "Name (Financial Section)",
            type: "text",
            placeholder: "Confirm client's name",
            // validation: { required: true }, // May not be needed if pre-filled
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_waiverEpsdtLtc",
            label: "Waiver/EPSDT/LTC",
            type: "text", // Or react-select if options are known
            placeholder: "Enter Waiver/EPSDT/LTC status",
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_supportCoordAgency",
            label: "Support Coordination Agency",
            type: "text",
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_providers",
            label: "Provider(s)",
            type: "text", // Could be paragraph if multiple providers are common
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_numPeopleInHome",
            label: "Number of People in Home",
            type: "text",
            validation: { required: true },
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_householdIncome", // This might be the total, or a field to enter main income
            label: "Household Income",
             type: "text", // Use number type for currency, handle formatting in UI
            prefix: "$", // Indicate currency
            placeholder: "Enter total monthly household income",
            validation: { required: true },
             // source: PDF Page 2 [cite: 2]
          },
           {
            id: "ff_fin_copcRequested",
            label: "COPC Requested $(Y/N)$", // Cost of Personal Care
            type: "radio",
            options: ["Yes", "No"],
            validation: { required: true },
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_incomeSsaSsi",
            label: "SSA/SSI",
            type: "text",
            prefix: "$",
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_incomeFoodStamps",
            label: "Food Stamps",
            type: "text",
            prefix: "$",
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_incomeFlexFamilyFund",
            label: "Flexible Family Fund",
            type: "text",
            prefix: "$",
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_incomeWages",
            label: "Wages",
            type: "text",
            prefix: "$",
             // source: PDF Page 2 [cite: 2]
          },
          {
            id: "ff_fin_incomeChildSupport",
            label: "Child Support",
            type: "text",
            prefix: "$",
             // source: PDF Page 2 [cite: 2]
          },
        ]
      },
      {
        id: "ff_step4",
        label: "Household Bills (Part 1)",
        icon: 'faHome', // Icon for home-related bills
        questions: [
          // Group 1 from PDF [cite: 3]
          { id: "ff_bill_mortgageRent", label: "Mortgage/Rent", type: "text", prefix: "$"},
          { id: "ff_bill_childcare", label: "Childcare", type: "text", prefix: "$"},
          { id: "ff_bill_insurances", label: "Insurance(s)", type: "text", prefix: "$"},
          { id: "ff_bill_loans", label: "Loan(s)", type: "text", prefix: "$"},
          { id: "ff_bill_utilities", label: "Utilities (water, gas, electricity)", type: "text", prefix: "$"},
          { id: "ff_bill_other1", label: "Other (Bills Group 1)", type: "text", prefix: "$"},
        ]
      },
      {
        id: "ff_step5",
        label: "Household Bills (Part 2)",
        icon: 'faReceipt', // Icon for general bills/receipts
        questions: [
           // Group 2 from PDF [cite: 4]
          { id: "ff_bill_cable", label: "Cable", type: "text", prefix: "$"},
          { id: "ff_bill_medical", label: "Medical", type: "text", prefix: "$"},
          { id: "ff_bill_food", label: "Food", type: "text", prefix: "$"},
          { id: "ff_bill_creditCards", label: "Credit Cards", type: "text", prefix: "$"},
          { id: "ff_bill_hygiene", label: "Hygiene", type: "text", prefix: "$"},
          { id: "ff_bill_other2", label: "Other (Bills Group 2)", type: "text", prefix: "$"},
        ]
      },
       {
        id: "ff_step6",
        label: "Household Bills (Part 3) & Totals",
        icon: 'faCalculator', // Icon for calculations/totals
        questions: [
           // Group 3 from PDF [cite: 5]
          { id: "ff_bill_carPayments", label: "Car Payment(s)", type: "text", prefix: "$"},
          { id: "ff_bill_phone", label: "Phone", type: "text", prefix: "$"},
          { id: "ff_bill_vehicleFuel", label: "Vehicle Fuel", type: "text", prefix: "$"},
          { id: "ff_bill_clothing", label: "Clothing", type: "text", prefix: "$"},
          { id: "ff_bill_other3Specify", label: "Other Bill (Specify)", type: "text"},
          { id: "ff_bill_other3Amount", label: "Other Bill Amount", type: "text", prefix: "$"}, // Amount for the specified other bill
          { id: "ff_bill_other4", label: "Other (Bills Group 3)", type: "text", prefix: "$"}, // This seems redundant if the one above is used, adjust as needed

          // Totals - Consider making these read-only and calculated in the UI [cite: 6]
          { id: "ff_totalIncome", label: "Total Income", type: "text", prefix: "$", disabled: true, placeholder: "Calculated..." },
          { id: "ff_totalExpenses", label: "Total Expenses", type: "text", prefix: "$", disabled: true, placeholder: "Calculated..." },
          { id: "ff_difference", label: "Difference", type: "text", prefix: "$", disabled: true, placeholder: "Calculated..." },
        ]
      },
       {
        id: "ff_step7",
        label: "Resources & Signature",
        icon: 'faSignature', // Reusing signature icon
        questions: [
          {
            id: "ff_otherResourcesExplored",
            label: "Other Resources Explored",
            type: "paragraph",
            placeholder: "Describe other resources explored",
             // source: PDF Page 2 [cite: 6]
          },
          {
            id: "ff_naturalSupports",
            label: "Natural Supports",
            type: "paragraph",
            placeholder: "Describe available natural supports",
             // source: PDF Page 2 [cite: 6]
          },
           { // Adding standard e-sign permission like in SCD_Form
            id: "ff_permissionToESign",
            label: "Do you give Sprout permission to e-sign your name?",
            type: "radio",
            options: ["Yes", "No"],
            validation: { required: true },
          },
        ],
      },
    ],
  },
  Monthly_Form: {
    form: {
      id: 192830,
      name: ""
    },
    steps: []
  }
};

// rulesConfig.ts

export const scdFormRules: Record<string, RulesConfig> = {
  Family_Funding_Request: {
    formId: "Family_Funding_Request", // Match the formId used in scdFormConfig
    validationRules: [
       {
         id: "ff-rule-esign-required",
         description: "User must choose 'Yes' for e-sign permission",
         when: [], // Apply always
         checks: [
           {
             field: "ff_permissionToESign", // Make sure the ID matches the question ID in your config
             operator: "EQUALS",
             value: "Yes",
           },
         ],
         errorMessage: "You must allow e-sign to complete this form.",
         errorField: "ff_permissionToESign", // Match the question ID
       },
    ],
  }
};

// dynamicSources.ts

export async function fetchDynamicOptions(
  source: string,
  authToken: string
): Promise<Array<{ value: string; label: string }> | null> {
  switch (source) {
    case "participants": {
      const participants = await getAllParticipants(authToken);
      if (!participants) return null;
      // Convert each participant into { value, label }
      const returnVal = participants.map((p) => ({
        value: p.id.toString(),
        label: `${p.firstName} ${p.lastName}`,
      }));

      return returnVal;
    }
    case "waiverTypes": {
        const waivers = await getWaiverTypes(authToken);
        if (!waivers) return null; 
        const returnVal = waivers.map((w) => ({
          value: w.id.toString(), 
          label: w.name
        }));
        console.log('Waiver Type Options', returnVal)
        return returnVal;
    }
    default:
      return null; 
  }
}
